import { useEffect, useRef } from "react";
import { ROUTES } from "@config/routes.config";

type Conversation = {
  id: string;
  channel: string;
  fanpageName?: string;
};

type Page = { id: string; name: string; messageSyncStatus?: string };
type MessagesByConversation = Record<string, unknown>;

type Params = {
  filteredConversations: Conversation[];
  selectedFanpageName?: string;
  selectedConversationId?: string;
  selectedChannel: string;
  conversations: Conversation[];
  selectConversation: (conversationId: string | undefined) => void;
  facebookPages: Page[];
  messagesByConversation: MessagesByConversation;
  setIsSyncing: (isSyncing: boolean) => void;
  navigate: (to: string, options?: { replace?: boolean }) => void;
  locationPathname: string;
  urlConversationId?: string;
  urlPlatform?: string;
  urlPageId?: string;
  isSwitchingFanpage: boolean;
  platformFromChannel: (channel: string) => string | undefined;
  resolvedPageId?: string;
  buildFanpageUrl: () => string;
};

export const useFanpageSelectionEffects = ({
  filteredConversations,
  selectedFanpageName,
  selectedConversationId,
  selectedChannel,
  conversations,
  selectConversation,
  facebookPages,
  messagesByConversation,
  setIsSyncing,
  navigate,
  locationPathname,
  urlConversationId,
  urlPlatform,
  urlPageId,
  isSwitchingFanpage,
  platformFromChannel,
  resolvedPageId,
  buildFanpageUrl,
}: Params) => {
  const prevFanpageRef = useRef<string | undefined>(undefined);
  const hasClearedNotSyncedRef = useRef(false);
  const hasAutoSelectedConversationRef = useRef(false);

  useEffect(() => {
    const currentPage = selectedFanpageName
      ? facebookPages.find((page) => page.name === selectedFanpageName)
      : null;
    const messageSyncStatus = currentPage?.messageSyncStatus;
    const isInProgress = messageSyncStatus === "IN_PROGRESS";
    const needsSync = messageSyncStatus === "NOT_SYNCED" || messageSyncStatus === "IN_PROGRESS";

    const fanpageChanged = prevFanpageRef.current !== selectedFanpageName;
    
    if (fanpageChanged) {
      if (messageSyncStatus !== "IN_PROGRESS") {
        setIsSyncing(false);
      }
      hasAutoSelectedConversationRef.current = false;
    }

    const isInitialMount = prevFanpageRef.current === undefined;    
    const shouldPreserveConversation = isInitialMount && urlConversationId;
    
    if (
      messageSyncStatus === "NOT_SYNCED" &&
      selectedConversationId &&
      (isInitialMount || fanpageChanged) &&
      !hasClearedNotSyncedRef.current &&
      !shouldPreserveConversation
    ) {
      selectConversation(undefined);
      if (!locationPathname.startsWith("/settings")) {
        navigate(ROUTES.HOME, { replace: true });
      }
      hasAutoSelectedConversationRef.current = false;
      hasClearedNotSyncedRef.current = true; 
      prevFanpageRef.current = selectedFanpageName;
      return; 
    }

    if (fanpageChanged || messageSyncStatus !== "NOT_SYNCED") {
      hasClearedNotSyncedRef.current = false;
    }
    
    prevFanpageRef.current = selectedFanpageName;
    
    if (isInProgress && selectedConversationId && !hasAutoSelectedConversationRef.current && !urlConversationId) {
      selectConversation(undefined);
      if (!locationPathname.startsWith("/settings")) {
        navigate(ROUTES.HOME, { replace: true });
      }
      return;
    }
    
    const isUrlConversationInCurrentList = urlConversationId 
      ? filteredConversations.some(conv => conv.id === urlConversationId)
      : false;

    // Auto-select first conversation when:
    // 1. User explicitly changes fanpage (fanpageChanged) - for better UX
    // 2. No conversation is selected and there are conversations available
    // 3. URL has conversationId that exists in current list
    // Do NOT auto-select when new conversations arrive from socket (only on initial load or fanpage change)
    if (
      selectedFanpageName &&
      filteredConversations.length > 0 &&
      !needsSync &&
      !selectedConversationId &&
      (!urlConversationId || !isUrlConversationInCurrentList)
    ) {
      // Auto-select on fanpage change or when conversations are first loaded
      const fanpageConversations = filteredConversations.filter(
        (conv) => conv.channel === selectedChannel
      );

      if (fanpageConversations.length > 0) {
        const conversationToSelect = fanpageConversations[0];
        const platform = platformFromChannel(selectedChannel);
        hasAutoSelectedConversationRef.current = true;
        selectConversation(conversationToSelect.id);    
        navigate(
          ROUTES.CONVERSATION(
            conversationToSelect.id,
            platform,
            resolvedPageId
          ),
          { replace: true }
        );
      }
    }
  }, [
    filteredConversations,
    selectedFanpageName,
    selectedConversationId,
    selectedChannel,
    conversations,
    selectConversation,
    facebookPages,
    messagesByConversation,
    setIsSyncing,
    navigate,
    locationPathname,
    urlConversationId,
    platformFromChannel,
    resolvedPageId,
  ]);

  useEffect(() => {
    const isSettingsRoute = locationPathname.startsWith("/settings");
    const isConversationRoute = locationPathname.startsWith("/conversations");
    const isValidForSelection = (conv?: Conversation) => {
      if (!conv) return false;
      if (conv.channel !== selectedChannel) return false;
      if (selectedFanpageName && conv.fanpageName && conv.fanpageName !== selectedFanpageName) {
        return false;
      }
      return true;
    };

    if (isSwitchingFanpage) {
      return;
    }

    if (selectedConversationId && selectedConversationId !== urlConversationId && !isSettingsRoute) {
      const selectedConversation = conversations.find((conv) => conv.id === selectedConversationId);
      if (isValidForSelection(selectedConversation)) {
        const platform = platformFromChannel(selectedChannel);
        navigate(
          ROUTES.CONVERSATION(selectedConversationId, platform, resolvedPageId),
          { replace: true }
        );
      } else {
        selectConversation(undefined);
        if (isConversationRoute) {
          navigate(ROUTES.HOME, { replace: true });
        }
      }
    } else if (selectedConversationId && selectedConversationId === urlConversationId && !isSettingsRoute) {
      const platform = platformFromChannel(selectedChannel);
      if (
        platform &&
        resolvedPageId &&
        (urlPlatform !== platform || urlPageId !== resolvedPageId)
      ) {
        navigate(
          ROUTES.CONVERSATION(selectedConversationId, platform, resolvedPageId),
          { replace: true }
        );
      }
    } else if (!selectedConversationId && urlConversationId && !isSettingsRoute) {
      const conversation = conversations.find((conv) => conv.id === urlConversationId);
      if (isValidForSelection(conversation)) {
        selectConversation(urlConversationId);
      } else {
        navigate(ROUTES.HOME, { replace: true });
      }
    } else if (!selectedConversationId && !isSettingsRoute) {
      if (isSwitchingFanpage) {
        return;
      }
      const platform = platformFromChannel(selectedChannel);
      if (
        selectedFanpageName &&
        platform &&
        resolvedPageId &&
        (urlPlatform !== platform || urlPageId !== resolvedPageId || urlConversationId)
      ) {
        navigate(buildFanpageUrl(), { replace: true });
      }
    } else if (!selectedConversationId && !urlConversationId && isConversationRoute) {
      if (isSwitchingFanpage) {
        return;
      }
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [
    selectedConversationId,
    urlConversationId,
    conversations,
    selectConversation,
    navigate,
    locationPathname,
    selectedChannel,
    selectedFanpageName,
    isSwitchingFanpage,
    platformFromChannel,
    resolvedPageId,
    urlPlatform,
    urlPageId,
    buildFanpageUrl,
  ]);

  return { hasClearedNotSyncedRef };
};
